import React, { useState, useEffect, useMemo, useRef } from "react";
import {
  Search,
  ChevronDown,
  ChevronUp,
  Copy,
  Check,
  Activity,
  ShieldAlert,
  ShieldCheck,
  RefreshCw,
  SlidersHorizontal,
  ChevronLeft,
  ChevronRight,
  Info,
  X,
  AlertCircle,
  CheckCircle2,
  Terminal,
  Upload,
} from "lucide-react";
import baseapi from "../../../../services/baseapi";
import { showError, showSuccess } from "../../../../utils/toast";
import ComplianceOverview from "./ComplianceOverview";

interface NewAssetComplianceDashboardProps {
  agent_id: string;
  organization_id: string;
  asset_id: string;
  client_id: string;
}

interface StandardSummary {
  id: number;
  standard_name: string;
  total_checks: number;
  passed: number;
  failed: number;
  score_pct: number;
  last_scanned_at: string;
}

interface GroupSummary {
  id: number;
  group_id: string;
  group_label: string;
  total_checks: number;
  passed: number;
  failed: number;
  score_pct: number;
  last_scanned_at: string;
}

interface CheckEvidenceItem {
  check_id: string;
  title: string | null;
  remediation: string | null;
  reported: boolean;
  status: boolean;
  value: any;
  os: string;
  applicability: string;
  regtype: string;
  target: string;
  group_ids?: string[] | string;
  group_labels?: string[] | string;
}

interface CheckItem {
  id: number;
  asset_id: number;
  agent_id: number;
  client_id: number;
  organization_id: number;
  check_id: string;
  status: boolean;
  value: string;
  created: string;
  updated: string;
  is_deleted: boolean;
  title: string;
  compliance_id: string;
  os: string;
  source: string;
  remediation: string;
  default_value: string;
  regtype: string;
  applicability: string;
  standards: string;
  group_ids: string;
  group_labels: string;
  requirement_status?: string;
  remediation_type?: string;
  logical_operator?: string;
  section?: string;
  sub_section?: string;
  check_ids?: string[];
  check_evidence?: CheckEvidenceItem[];
  evidence_file?: string | null;
  evidence_url?: string | null;
  evidence_notes?: string | null;
  uploaded_by?: string | null;
  reviewed_at?: string | null;
}

// Reusable Custom Dropdown Component
const CustomDropdown = <T,>({
  label,
  value,
  options,
  onChange,
  placeholder = "Select Option",
  className = "",
}: {
  label?: string;
  value: T | null;
  options: { value: T | null; label: string }[];
  onChange: (val: T | null) => void;
  placeholder?: string;
  className?: string;
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handleOutsideClick = (e: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(e.target as Node)) {
        setIsOpen(false);
      }
    };
    document.addEventListener("mousedown", handleOutsideClick);
    return () => document.removeEventListener("mousedown", handleOutsideClick);
  }, []);

  const selectedOption = options.find(opt => opt.value === value);

  return (
    <div ref={containerRef} className={`relative inline-block text-left ${className}`}>
      <button
        type="button"
        onClick={() => setIsOpen(!isOpen)}
        className="flex items-center justify-between w-full bg-white dark:bg-gray-900 border border-slate-200 dark:border-gray-700 hover:bg-slate-50 dark:hover:bg-gray-800 text-xs font-bold text-slate-700 dark:text-gray-200 px-3 py-1.5 rounded-lg focus:outline-none focus:border-orange-500 cursor-pointer transition select-none min-w-[170px] shadow-sm dark:shadow-none"
      >
        <span className="truncate">
          {label ? `${label}: ` : ""}
          {selectedOption ? selectedOption.label : placeholder}
        </span>
        <ChevronDown className={`w-3.5 h-3.5 ml-2 text-slate-400 dark:text-gray-400 transition-transform ${isOpen ? "rotate-180" : ""}`} />
      </button>

      {isOpen && (
        <div className="absolute right-0 mt-1 w-full min-w-[190px] bg-white dark:bg-gray-900 border border-slate-200 dark:border-gray-700 rounded-lg shadow-xl overflow-hidden z-50 animate-in fade-in slide-in-from-top-1 duration-150">
          <div className="py-1 max-h-60 overflow-y-auto divide-y divide-slate-100 dark:divide-gray-800">
            {options.map((opt, index) => {
              const isSelected = opt.value === value;
              return (
                <button
                  key={index}
                  type="button"
                  onClick={() => {
                    onChange(opt.value);
                    setIsOpen(false);
                  }}
                  className={`w-full text-left px-3.5 py-2 text-xs font-semibold flex items-center justify-between cursor-pointer transition ${isSelected
                    ? "bg-orange-50 dark:bg-gradient-to-r dark:from-orange-600/10 dark:to-amber-500/10 text-orange-600 dark:text-orange-400 font-extrabold"
                    : "text-slate-600 dark:text-gray-300 hover:bg-slate-50 dark:hover:bg-gray-800 hover:text-slate-900 dark:hover:text-white"
                    }`}
                >
                  <span className="truncate">{opt.label}</span>
                  {isSelected && <Check className="w-3.5 h-3.5 text-orange-600 dark:text-orange-400 flex-shrink-0" />}
                </button>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
};

const NewAssetComplianceDashboard: React.FC<NewAssetComplianceDashboardProps> = ({
  asset_id,
}) => {
  const [standards, setStandards] = useState<StandardSummary[]>([]);
  const [groups, setGroups] = useState<GroupSummary[]>([]);
  const [checks, setChecks] = useState<CheckItem[]>([]);
  const [totalChecksCount, setTotalChecksCount] = useState<number>(0);
  const [loadingSummary, setLoadingSummary] = useState<boolean>(true);
  const [loadingChecks, setLoadingChecks] = useState<boolean>(false);

  // Active Standard & Filters (Starts as null so we show Overview landing page)
  const [selectedStandard, setSelectedStandard] = useState<StandardSummary | null>(null);
  const [selectedGroup, setSelectedGroup] = useState<GroupSummary | null>(null);
  const [searchQuery, setSearchQuery] = useState<string>("");
  const [debouncedSearchQuery, setDebouncedSearchQuery] = useState<string>("");
  const [statusFilter, setStatusFilter] = useState<"all" | "compliant" | "non-compliant" | "manual">("all");

  // Expanded Check details
  const [expandedCheckId, setExpandedCheckId] = useState<number | null>(null);
  const [copiedField, setCopiedField] = useState<string | null>(null);
  const [isRefreshing, setIsRefreshing] = useState<boolean>(false);

  // Detailed tab within check details (values vs remediation instructions)
  const [checkTab, setCheckTab] = useState<Record<number, "audit" | "remediation">>({});

  // Pagination
  const [page, setPage] = useState<number>(1);
  const [pageSize, setPageSize] = useState<number>(10);

  // Evidence upload modal states
  const [evidenceModalOpen, setEvidenceModalOpen] = useState<boolean>(false);
  const [selectedCheckForEvidence, setSelectedCheckForEvidence] = useState<any | null>(null);
  const [evidenceFile, setEvidenceFile] = useState<File | null>(null);
  const [evidenceNotes, setEvidenceNotes] = useState<string>("");
  const [isUploading, setIsUploading] = useState<boolean>(false);
  const [evidenceError, setEvidenceError] = useState<string | null>(null);
  const evidenceFileInputRef = useRef<HTMLInputElement>(null);

  const handleOpenEvidenceModal = (check: any) => {
    setSelectedCheckForEvidence(check);
    setEvidenceFile(null);
    setEvidenceNotes("");
    setEvidenceError(null);
    setEvidenceModalOpen(true);
  };

  const handleCloseEvidenceModal = () => {
    setEvidenceModalOpen(false);
    setSelectedCheckForEvidence(null);
    setEvidenceFile(null);
    setEvidenceNotes("");
    setEvidenceError(null);
  };

  const handleEvidenceFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files && e.target.files.length > 0) {
      const selectedFile = e.target.files[0];
      const validTypes = ["image/png", "image/jpeg", "image/jpg"];
      if (!validTypes.includes(selectedFile.type)) {
        setEvidenceError("Please upload a valid image file (PNG or JPEG)");
        setEvidenceFile(null);
        return;
      }
      setEvidenceFile(selectedFile);
      setEvidenceError(null);
    }
  };

  const handleUploadEvidence = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!selectedCheckForEvidence) return;

    if (!evidenceFile) {
      setEvidenceError("Please select an image file to upload.");
      return;
    }

    try {
      setIsUploading(true);
      const formData = new FormData();

      const standardsArr = selectedCheckForEvidence.parsedStandards || [];
      const standardName = standardsArr.length > 0 ? standardsArr[0] : (selectedStandard?.standard_name || "CIS");

      formData.append("standard", standardName);
      formData.append("check_id", selectedCheckForEvidence.check_id);
      if (evidenceNotes.trim()) {
        formData.append("notes", evidenceNotes.trim());
      }
      formData.append("file", evidenceFile);

      const res = await baseapi.post(`/asset_compliance/asset/${asset_id}/requirements/evidence`, formData, {
        headers: {
          "Content-Type": "multipart/form-data",
        },
      });

      if (res.data && res.data.status) {
        showSuccess(res.data.message || "Evidence uploaded successfully.");
        handleCloseEvidenceModal();
        await Promise.all([
          fetchSummary(),
          fetchChecks(),
        ]);
      } else {
        showError(res.data?.message || "Failed to upload evidence.");
      }
    } catch (err: any) {
      showError(err.response?.data?.message || err.message || "Error uploading evidence.");
    } finally {
      setIsUploading(false);
    }
  };

  // Debounce search query changes
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearchQuery(searchQuery);
    }, 400);
    return () => clearTimeout(timer);
  }, [searchQuery]);

  // Fetch summary (standards & groups)
  const fetchSummary = async () => {
    try {
      setLoadingSummary(true);
      const res = await baseapi.get(`/asset_compliance/asset/${asset_id}/summary`);
      if (res.data && res.data.status) {
        const fetchedStandards = res.data.standards || [];
        setStandards(fetchedStandards);

        const fetchedGroups = res.data.groups || [];
        setGroups(fetchedGroups);

        // Update selectedStandard dynamically to refresh counters/score circle
        setSelectedStandard(prevSelected => {
          if (!prevSelected) return null;
          return fetchedStandards.find((s: StandardSummary) => s.standard_name === prevSelected.standard_name) || prevSelected;
        });

        // Update selectedGroup dynamically to refresh toolbar stats
        setSelectedGroup(prevGroup => {
          if (!prevGroup) return null;
          return fetchedGroups.find((g: GroupSummary) => g.group_id === prevGroup.group_id) || prevGroup;
        });
      } else {
        showError(res.data?.message || "Failed to load compliance summary.");
      }
    } catch (err: any) {
      showError(err.message || "Error fetching compliance summary.");
    } finally {
      setLoadingSummary(false);
    }
  };

  // Fetch checks list with API level pagination & filtering (standard, group_id, status, search, skip, limit)
  const fetchChecks = async () => {
    if (!selectedStandard) return;
    try {
      setLoadingChecks(true);

      const params: any = {
        limit: pageSize,
        skip: (page - 1) * pageSize,
        standard: selectedStandard.standard_name,
      };

      if (selectedGroup) {
        params.group_id = selectedGroup.group_id;
      }

      if (statusFilter !== "all") {
        if (statusFilter === "compliant") {
          params.status = true;
        } else if (statusFilter === 'non-compliant') {
          params.status = false;
        } else if (statusFilter === "manual") {
          params.status = false;
          params.remediation_type = "manual";
        }

      }

      if (debouncedSearchQuery.trim()) {
        params.search = debouncedSearchQuery.trim();
      }

      const res = await baseapi.get(`/asset_compliance/asset/${asset_id}/checks`, { params });
      if (res.data && res.data.data) {
        setChecks(res.data.data);
        setTotalChecksCount(res.data.total || 0);
      } else {
        showError("Failed to load compliance checks.");
      }
    } catch (err: any) {
      showError(err.message || "Error fetching compliance checks.");
    } finally {
      setLoadingChecks(false);
    }
  };

  const handleRefresh = async () => {
    try {
      setIsRefreshing(true);
      await Promise.all([
        fetchSummary(),
        selectedStandard ? fetchChecks() : Promise.resolve(),
      ]);
    } catch (err: any) {
      showError(err.message || "Error refreshing compliance data.");
    } finally {
      setIsRefreshing(false);
    }
  };

  // Initial load
  useEffect(() => {
    if (asset_id) {
      fetchSummary();
    }
  }, [asset_id]);

  // Load checks when parameters change
  useEffect(() => {
    if (asset_id && selectedStandard) {
      fetchChecks();
    }
  }, [asset_id, selectedStandard?.standard_name, selectedGroup?.group_id, statusFilter, debouncedSearchQuery, page, pageSize]);

  // Reset page when filters change
  useEffect(() => {
    if (page !== 1) {
      setPage(1);
    }
  }, [debouncedSearchQuery, statusFilter, pageSize]);

  // Reset group when standard changes
  const handleStandardChange = (std: StandardSummary | null) => {
    setSelectedStandard(std);
    setSelectedGroup(null);
    setPage(1);
  };

  const handleGroupChange = (grp: GroupSummary | null) => {
    setSelectedGroup(grp);
    setPage(1);
  };

  const handleStatusFilterChange = (status: "all" | "compliant" | "non-compliant" | "manual") => {
    setStatusFilter(status);
    setPage(1);
  };

  // Parse stringified JSON arrays in checks for display
  const parsedChecks = useMemo(() => {
    return checks.map(c => {
      let standardsArr: string[] = [];
      let groupLabelsArr: string[] = [];
      let groupIdsArr: string[] = [];
      try {
        standardsArr = c.standards ? JSON.parse(c.standards) : [];
      } catch (e) {
        standardsArr = c.standards ? [c.standards] : [];
      }
      try {
        groupLabelsArr = c.group_labels ? JSON.parse(c.group_labels) : [];
      } catch (e) {
        groupLabelsArr = c.group_labels ? [c.group_labels] : [];
      }
      try {
        groupIdsArr = c.group_ids ? JSON.parse(c.group_ids) : [];
      } catch (e) {
        groupIdsArr = c.group_ids ? [c.group_ids] : [];
      }

      return {
        ...c,
        parsedStandards: standardsArr,
        parsedGroupLabels: groupLabelsArr,
        parsedGroupIds: groupIdsArr
      };
    });
  }, [checks]);

  const totalPages = Math.max(1, Math.ceil(totalChecksCount / pageSize));

  // Copy to clipboard helper
  const handleCopy = (text: string, fieldId: string) => {
    navigator.clipboard.writeText(text);
    setCopiedField(fieldId);
    setTimeout(() => setCopiedField(null), 2000);
  };

  // Safe parse current value representation
  const renderCurrentValue = (valStr: string) => {
    try {
      const parsed = JSON.parse(valStr);
      if (Array.isArray(parsed)) {
        return parsed.join(", ");
      }
      return String(parsed);
    } catch {
      return valStr || "-";
    }
  };

  // Circular gauge config
  const gaugeRadius = 24;
  const currentScore = selectedStandard?.score_pct || 0;

  // Visual highlights for score
  const getScoreColors = (score: number) => {
    if (score >= 80) return { start: "#10b981", end: "#34d399", border: "border-emerald-200 dark:border-emerald-500/20", bg: "bg-emerald-50 dark:bg-emerald-500/10", text: "text-emerald-600 dark:text-emerald-400" };
    if (score >= 50) return { start: "#f59e0b", end: "#fbbf24", border: "border-amber-200 dark:border-amber-500/20", bg: "bg-amber-50 dark:bg-amber-500/10", text: "text-amber-600 dark:text-amber-400" };
    return { start: "#ef4444", end: "#f87171", border: "border-rose-200 dark:border-rose-500/20", bg: "bg-rose-50 dark:bg-rose-500/10", text: "text-rose-600 dark:text-rose-400" };
  };

  const scoreTheme = getScoreColors(currentScore);

  // Map standards for custom dropdown options (including Overview)
  const standardOptions = useMemo(() => {
    return [
      { value: null, label: "Overview" },
      ...standards.map(std => ({
        value: std,
        label: std.standard_name
      }))
    ];
  }, [standards]);

  // Map groups for custom dropdown options
  const groupOptions = useMemo(() => {
    return [
      { value: null, label: "All Security Groups" },
      ...groups.map(grp => ({
        value: grp,
        label: grp.group_label
      }))
    ];
  }, [groups]);

  // Map status options for custom dropdown
  const statusOptions = [
    { value: "all", label: "All Statuses" },
    { value: "compliant", label: "Compliant" },
    { value: "non-compliant", label: "Non-Compliant" },
    { value: "manual", label: "Manual" }
  ];

  if (loadingSummary && standards.length === 0) {
    return (
      <div className="flex flex-col items-center justify-center min-h-[300px] gap-2 text-slate-500 dark:text-gray-400">
        <RefreshCw className="w-8 h-8 animate-spin text-orange-500" />
        <p className="text-sm font-semibold">Loading compliance dashboard...</p>
      </div>
    );
  }

  return (
    <div className="space-y-3.5 text-slate-800 dark:text-gray-200 p-1">
      {/* Header View */}
      <div className="flex flex-row items-center justify-between gap-4 border-b border-slate-200 dark:border-gray-700 pb-3">
        <div className="flex items-center">
          <div className="p-1.5 bg-orange-600 rounded mr-2.5 shadow-sm">
            <Activity className="w-4 h-4 text-white" />
          </div>
          <div>
            <h2 className="text-sm font-bold text-slate-900 dark:text-white leading-tight">
              {selectedStandard ? `${selectedStandard.standard_name} Audit` : "Compliance Overview"}
            </h2>
            <p className="text-[10px] text-slate-500 dark:text-gray-400 mt-0.5">
              {selectedStandard
                ? `Review system configurations and audit status against the ${selectedStandard.standard_name} standard.`
                : "Overview of scanned compliance benchmarks and security groups configurations."
              }
            </p>
          </div>
        </div>

        {/* Standard Selector & Overview button on the right */}
        <div className="flex items-center gap-2">
          <button
            type="button"
            onClick={handleRefresh}
            disabled={isRefreshing || loadingSummary || (!!selectedStandard && loadingChecks)}
            className="p-2 bg-white dark:bg-gray-800 hover:bg-slate-50 dark:hover:bg-gray-700 text-slate-600 dark:text-gray-300 hover:text-slate-900 dark:hover:text-white border border-slate-200 dark:border-gray-700 rounded-md transition-colors shadow-sm flex items-center justify-center disabled:opacity-50 h-8 cursor-pointer"
            title="Refresh Compliance Data"
          >
            <RefreshCw className={`w-4 h-4 ${isRefreshing || loadingSummary || (!!selectedStandard && loadingChecks) ? "animate-spin text-orange-500" : ""}`} />
          </button>
          {selectedStandard && (
            <button
              onClick={() => setSelectedStandard(null)}
              className="text-xs bg-orange-600 hover:bg-orange-500 border border-orange-500 hover:border-orange-400 px-3 py-1.5 rounded-lg text-white cursor-pointer font-bold transition duration-200 shadow-sm"
            >
              Overview
            </button>
          )}
          {standards.length > 0 && (
            <div className="flex gap-1.5">
              <CustomDropdown
                label="Standard"
                value={selectedStandard}
                options={standardOptions}
                onChange={(val) => {
                  handleStandardChange(val);
                }}
                placeholder="Select Standard"
              />
            </div>
          )}
        </div>
      </div>

      {/* Main Content Pane */}
      {!selectedStandard ? (
        /* Render ComplianceOverview component from the new file */
        <ComplianceOverview
          standards={standards}
          groups={groups}
          onSelectStandard={handleStandardChange}
        />
      ) : (
        /* Detailed Checks View for the selected standard */
        <>
          {/* Dashboard Summary Cards */}
          <div className="grid grid-cols-1 md:grid-cols-4 gap-3">
            {/* Score circle card */}
            <div className="bg-white dark:bg-gray-800 border border-slate-200 dark:border-gray-700/60 rounded-xl p-3 flex items-center gap-3 shadow-sm dark:shadow-none">
              <div className="relative flex items-center justify-center flex-shrink-0">
                <svg className="w-16 h-16 transform -rotate-90">
                  <circle
                    cx="32"
                    cy="32"
                    r={gaugeRadius}
                    className="text-slate-200 dark:text-gray-700"
                    strokeWidth="5"
                    stroke="currentColor"
                    fill="transparent"
                  />
                  <circle
                    cx="32"
                    cy="32"
                    r={gaugeRadius}
                    className="transition-all duration-1000 ease-out"
                    strokeWidth="5"
                    strokeDasharray={2 * Math.PI * gaugeRadius}
                    strokeDashoffset={2 * Math.PI * gaugeRadius - (currentScore / 100) * 2 * Math.PI * gaugeRadius}
                    strokeLinecap="round"
                    stroke="url(#complianceScoreGradSmall)"
                    fill="transparent"
                  />
                  <defs>
                    <linearGradient id="complianceScoreGradSmall" x1="0%" y1="0%" x2="100%" y2="100%">
                      <stop offset="0%" stopColor={scoreTheme.start} />
                      <stop offset="100%" stopColor={scoreTheme.end} />
                    </linearGradient>
                  </defs>
                </svg>
                <div className="absolute flex flex-col items-center">
                  <span className="text-sm font-black text-slate-900 dark:text-white leading-none">
                    {currentScore.toFixed(0)}%
                  </span>
                </div>
              </div>
              <div>
                <h4 className="text-[10px] font-bold text-slate-500 dark:text-gray-400 uppercase tracking-wider">Compliance Index</h4>
                <p className={`text-sm font-black mt-0.5 ${scoreTheme.text}`}>
                  {currentScore >= 80 ? "Compliant" : currentScore >= 50 ? "Review Needed" : "Non-Compliant"}
                </p>
                <span className="text-[9px] text-slate-400 dark:text-gray-500 block">
                  Scanned: {selectedStandard.last_scanned_at ? new Date(selectedStandard.last_scanned_at).toLocaleDateString() : "Never"}
                </span>
              </div>
            </div>

            {/* Total checks card */}
            <div className="bg-white dark:bg-gray-800 border border-slate-200 dark:border-gray-700/60 rounded-xl p-3 flex items-center justify-between shadow-sm dark:shadow-none">
              <div>
                <span className="text-[10px] font-bold text-slate-500 dark:text-gray-400 uppercase tracking-wider">Total Checks</span>
                <p className="text-xl font-black text-slate-900 dark:text-white mt-1 leading-none">{selectedStandard.total_checks}</p>
                <span className="text-[9px] text-slate-400 dark:text-gray-500 block mt-1">Audit requirements checked</span>
              </div>
              <div className="p-1.5 rounded-lg bg-slate-100 dark:bg-gray-900 text-slate-500 dark:text-gray-400 border border-slate-200 dark:border-gray-700/30">
                <Activity className="w-4 h-4" />
              </div>
            </div>

            {/* Passed checks card */}
            <div className="bg-white dark:bg-gray-800 border border-slate-200 dark:border-gray-700/60 rounded-xl p-3 flex items-center justify-between shadow-sm dark:shadow-none">
              <div>
                <span className="text-[10px] font-bold text-slate-500 dark:text-gray-400 uppercase tracking-wider">Passed Requirements</span>
                <p className="text-xl font-black text-emerald-600 dark:text-emerald-400 mt-1 leading-none">{selectedStandard.passed}</p>
                <span className="text-[9px] text-emerald-600 dark:text-emerald-500 block mt-1">Compliant configurations</span>
              </div>
              <div className="p-1.5 rounded-lg bg-emerald-50 dark:bg-emerald-500/10 text-emerald-600 dark:text-emerald-400 border border-emerald-200 dark:border-emerald-500/20">
                <ShieldCheck className="w-4 h-4" />
              </div>
            </div>

            {/* Failed checks card */}
            <div className="bg-white dark:bg-gray-800 border border-slate-200 dark:border-gray-700/60 rounded-xl p-3 flex items-center justify-between shadow-sm dark:shadow-none">
              <div>
                <span className="text-[10px] font-bold text-slate-500 dark:text-gray-400 uppercase tracking-wider">Failed Audit Checks</span>
                <p className="text-xl font-black text-rose-600 dark:text-rose-400 mt-1 leading-none">{selectedStandard.failed}</p>
                <span className="text-[9px] text-rose-600 dark:text-rose-500 block mt-1">Remediation recommended</span>
              </div>
              <div className="p-1.5 rounded-lg bg-rose-50 dark:bg-rose-500/10 text-rose-600 dark:text-rose-400 border border-rose-200 dark:border-rose-500/20">
                <ShieldAlert className="w-4 h-4" />
              </div>
            </div>
          </div>

          {/* Checklist Table Area */}
          <div className="bg-white dark:bg-gray-800/40 border border-slate-200 dark:border-gray-700/60 rounded-xl overflow-hidden shadow-sm dark:shadow-none">
            {/* Filter toolbar */}
            <div className="p-2.5 border-b border-slate-200 dark:border-gray-700/60 bg-slate-50 dark:bg-gray-800/80 flex flex-col md:flex-row md:items-center justify-between gap-3">
              <div className="flex flex-col sm:flex-row sm:items-center gap-2.5 flex-grow">
                {/* Passed / Failed / Total Counters Badge */}
                <div className="flex items-center gap-2 text-[10px] text-slate-600 dark:text-gray-400 bg-white dark:bg-gray-900 px-2.5 py-1.5 rounded-lg border border-slate-200 dark:border-gray-700 shrink-0 shadow-sm dark:shadow-none">
                  <span>
                    Passed: <span className="text-emerald-600 dark:text-emerald-400 font-extrabold">{selectedGroup ? selectedGroup.passed : selectedStandard.passed}</span>
                  </span>
                  <span className="text-slate-300 dark:text-gray-600">|</span>
                  <span>
                    Failed: <span className="text-rose-600 dark:text-rose-400 font-extrabold">{selectedGroup ? selectedGroup.failed : selectedStandard.failed}</span>
                  </span>
                  <span className="text-slate-300 dark:text-gray-600">|</span>
                  <span>
                    Total: <span className="text-slate-900 dark:text-white font-extrabold">{totalChecksCount}</span>
                  </span>
                </div>

                {/* Group Selector Dropdown */}
                <div className="flex items-center gap-1.5">
                  <CustomDropdown
                    label="Group"
                    value={selectedGroup}
                    options={groupOptions}
                    onChange={handleGroupChange}
                    placeholder="All Security Groups"
                  />
                </div>

                {/* Status Selector Dropdown */}
                <div className="flex items-center gap-1.5">
                  <CustomDropdown
                    label="Status"
                    value={statusFilter}
                    options={statusOptions}
                    onChange={(val) => {
                      if (val) handleStatusFilterChange(val as "all" | "compliant" | "non-compliant" | "manual");
                    }}
                    placeholder="All Statuses"
                  />
                </div>
              </div>

              {/* Search query input & Clear Filters on Right side */}
              <div className="flex items-center gap-3 justify-between sm:justify-end">
                <div className="relative min-w-[200px] flex-grow md:flex-initial">
                  <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-slate-400 dark:text-gray-500" />
                  <input
                    type="text"
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    placeholder="Search checks..."
                    className="w-full bg-white dark:bg-gray-900 border border-slate-200 dark:border-gray-700 hover:border-slate-300 dark:hover:border-gray-650 focus:border-orange-500 rounded-lg pl-8 pr-7 py-1.5 text-xs text-slate-900 dark:text-gray-100 placeholder-slate-400 dark:placeholder-gray-500 outline-none transition-all shadow-sm dark:shadow-none"
                  />
                  {searchQuery && (
                    <button
                      onClick={() => setSearchQuery("")}
                      className="absolute right-2.5 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-700 dark:text-gray-500 dark:hover:text-white cursor-pointer"
                    >
                      <X className="w-3.5 h-3.5" />
                    </button>
                  )}
                </div>
                {(searchQuery || selectedGroup || statusFilter !== "all") && (
                  <button
                    onClick={() => {
                      setSearchQuery("");
                      handleGroupChange(null);
                      handleStatusFilterChange("all");
                    }}
                    className="text-[11px] font-black text-orange-600 dark:text-orange-400 hover:text-orange-500 dark:hover:text-orange-300 transition-all flex items-center gap-1 cursor-pointer shrink-0"
                  >
                    <SlidersHorizontal className="w-3 h-3" />
                    Clear Filters
                  </button>
                )}
              </div>
            </div>

            {/* Checks List / Table */}
            {loadingChecks ? (
              <div className="flex flex-col items-center justify-center p-8 text-slate-500 dark:text-gray-400 gap-1.5">
                <RefreshCw className="w-5 h-5 animate-spin text-orange-500" />
                <span className="text-xs font-semibold">Loading checks...</span>
              </div>
            ) : checks.length === 0 ? (
              <div className="flex flex-col items-center justify-center p-12 text-slate-400 dark:text-gray-500 text-center gap-1.5">
                <Info className="w-6 h-6 text-slate-400 dark:text-gray-600" />
                <p className="text-xs font-semibold">No compliance checks found.</p>
              </div>
            ) : (
              <div className="divide-y divide-slate-100 dark:divide-gray-700/60">
                {parsedChecks.map((check) => {
                  const isExpanded = expandedCheckId === check.id;
                  const displayId = check.compliance_id || check.check_id;
                  const activeDetailTab = checkTab[check.id] || "audit";

                  return (
                    <div
                      key={check.id}
                      className={`transition-all duration-200 ${isExpanded ? "bg-slate-50/50 dark:bg-gray-800/10" : "hover:bg-slate-50/80 dark:hover:bg-gray-800/20"
                        }`}
                    >
                      {/* Accordion Trigger Summary Header */}
                      <div
                        onClick={() => setExpandedCheckId(isExpanded ? null : check.id)}
                        className="p-2.5 flex items-center justify-between gap-3 cursor-pointer select-none"
                      >
                        <div className="flex items-start gap-2.5 min-w-0">
                          {/* Compliance / Check ID column */}
                          <div className="flex items-center gap-2.5 flex-shrink-0">

                            <div className="flex flex-col items-start gap-0.5">
                              <span className="text-[10px] font-bold text-slate-900 dark:text-white font-mono bg-slate-100 dark:bg-gray-900 border border-slate-200 dark:border-gray-700 px-1 py-0.2 rounded font-black">
                                {displayId}
                              </span>
                              <span className="text-[8px] text-slate-500 dark:text-gray-100 uppercase tracking-wider font-semibold">
                                {check.source || "System"}
                              </span>
                            </div>
                          </div>

                          {/* Title / Description */}
                          <div className="min-w-0">
                            <p className="text-xs font-bold text-slate-900 dark:text-gray-200 hover:text-orange-600 dark:hover:text-white leading-tight break-words transition-colors">
                              {check.title}
                            </p>
                            <div className="flex items-center gap-1.5 mt-1 flex-wrap">
                              {/* Standards badges */}
                              {check.parsedStandards.map((std, i) => (
                                <span key={i} className="text-[8px] bg-orange-50 dark:bg-orange-950/20 text-orange-600 dark:text-orange-400 px-1 py-0.1 rounded font-bold border border-orange-200 dark:border-orange-900/20">
                                  {std}
                                </span>
                              ))}
                              {/* Group labels badges */}
                              {check.parsedGroupLabels.map((grp, i) => (
                                <span key={i} className="text-[8px] bg-blue-50 dark:bg-blue-950/20 text-blue-600 dark:text-blue-400 px-1.5 py-0.1 rounded font-bold border border-blue-200 dark:border-blue-900/20">
                                  {grp}
                                </span>
                              ))}
                            </div>
                          </div>
                        </div>

                        {/* Status Badge + Expanding Chevron */}
                        <div className="flex items-center gap-3 flex-shrink-0">
                          {check.remediation_type === "manual" && check.requirement_status === "manual_pending" && (
                            <button
                              onClick={(e) => {
                                e.stopPropagation();
                                handleOpenEvidenceModal(check);
                              }}
                              className="p-2 rounded-lg bg-orange-600 hover:bg-orange-500 text-white cursor-pointer transition duration-200 shrink-0 shadow-md shadow-orange-600/10"
                              title="Upload Manual Evidence"
                            >
                              <Upload className="w-4 h-4" />
                            </button>
                          )}
                          {check.status ? (
                            <span className="inline-flex items-center gap-1 px-1.5 py-0.5 bg-emerald-50 dark:bg-emerald-500/10 text-emerald-600 dark:text-emerald-400 rounded-md text-[10px] font-bold border border-emerald-200 dark:border-emerald-500/20">
                              <CheckCircle2 className="w-3 h-3" />
                              Compliant
                            </span>
                          ) : check.requirement_status === "manual_pending" ? (
                            <span className="inline-flex items-center gap-1 px-1.5 py-0.5 bg-amber-50 dark:bg-amber-500/10 text-amber-600 dark:text-amber-400 rounded-md text-[10px] font-bold border border-amber-200 dark:border-amber-500/20">
                              <AlertCircle className="w-3 h-3" />
                              Manual Pending
                            </span>
                          ) : (
                            <span className="inline-flex items-center gap-1 px-1.5 py-0.5 bg-rose-50 dark:bg-rose-500/10 text-rose-600 dark:text-rose-400 rounded-md text-[10px] font-bold border border-rose-200 dark:border-rose-500/20 animate-pulse">
                              <AlertCircle className="w-3 h-3" />
                              Non-Compliant
                            </span>
                          )}
                          <div className="text-slate-400 dark:text-gray-500 transition-transform">
                            {isExpanded ? (
                              <ChevronUp className="w-4 h-4 text-orange-500 dark:text-orange-400" />
                            ) : (
                              <ChevronDown className="w-4 h-4" />
                            )}
                          </div>
                        </div>
                      </div>

                      {/* Expandable Details Container */}
                      {isExpanded && (
                        <div className="px-2.5 pb-3 pt-0.5 border-t border-slate-200 dark:border-gray-700/40 bg-slate-50/70 dark:bg-gray-900/20">
                          {/* Inner Tabs Navigation */}
                          <div className="flex items-center gap-2 border-b border-slate-200 dark:border-gray-800/80 mb-3 pb-1">
                            <button
                              onClick={() => setCheckTab(prev => ({ ...prev, [check.id]: "audit" }))}
                              className={`px-3 py-1.5 text-xs font-bold transition-all border-b-2 cursor-pointer ${activeDetailTab === "audit"
                                ? "border-orange-500 text-orange-600 dark:text-orange-400"
                                : "border-transparent text-slate-500 hover:text-slate-800 dark:text-gray-400 dark:hover:text-gray-200"
                                }`}
                            >
                              <div className="flex items-center gap-1">
                                <Terminal className="w-3.5 h-3.5" />
                                Audit Evidence
                              </div>
                            </button>
                            {check.remediation && (
                              <button
                                onClick={() => setCheckTab(prev => ({ ...prev, [check.id]: "remediation" }))}
                                className={`px-3 py-1.5 text-xs font-bold transition-all border-b-2 cursor-pointer ${activeDetailTab === "remediation"
                                  ? "border-orange-500 text-orange-600 dark:text-orange-400"
                                  : "border-transparent text-slate-500 hover:text-slate-800 dark:text-gray-400 dark:hover:text-gray-200"
                                  }`}
                              >
                                <div className="flex items-center gap-1">
                                  <Info className="w-3.5 h-3.5" />
                                  Remediation Steps
                                </div>
                              </button>
                            )}
                          </div>

                          {/* Tab Content 1: Audit & Evidence Metadata */}
                          {activeDetailTab === "audit" && (
                            <div className="space-y-2.5">
                              {/* Evidence Block */}
                              <div className="p-2.5 bg-white dark:bg-gray-950/15 rounded-lg border border-slate-200 dark:border-gray-700/40 space-y-2 mt-2.5 shadow-sm dark:shadow-none">
                                <span className="text-[9px] font-bold text-slate-500 dark:text-gray-400 uppercase tracking-widest block mb-1.5">
                                  {check.remediation_type === "manual" ? "Manual Compliance Evidence" : "Audited Configuration Values"}
                                </span>
                                {check.remediation_type === "manual" ? (
                                  <div className="space-y-3">
                                    {/* Upload info / Metadata row */}
                                    {(check.uploaded_by || check.reviewed_at) ? (
                                      <div className="grid grid-cols-1 md:grid-cols-2 gap-3 text-xs bg-slate-50 dark:bg-gray-900/40 p-2.5 rounded border border-slate-200 dark:border-gray-800">
                                        {check.uploaded_by && (
                                          <div>
                                            <span className="text-[9px] text-slate-500 uppercase tracking-wider block font-bold">Uploaded By</span>
                                            <span className="text-slate-900 dark:text-gray-200 font-semibold">{check.uploaded_by}</span>
                                          </div>
                                        )}
                                        {check.reviewed_at && (
                                          <div>
                                            <span className="text-[9px] text-slate-500 uppercase tracking-wider block font-bold">Reviewed / Uploaded At</span>
                                            <span className="text-slate-900 dark:text-gray-200 font-semibold">
                                              {new Date(check.reviewed_at).toLocaleString()}
                                            </span>
                                          </div>
                                        )}
                                      </div>
                                    ) : (
                                      <div className="p-3 text-center text-slate-400 dark:text-gray-500 border border-dashed border-slate-200 dark:border-gray-800 rounded-lg text-xs font-semibold">
                                        No evidence metadata available.
                                      </div>
                                    )}

                                    {/* Notes block */}
                                    {check.evidence_notes && (
                                      <div className="p-2.5 bg-slate-50 dark:bg-gray-900/30 rounded border border-slate-200 dark:border-gray-800 text-xs">
                                        <span className="text-[9px] text-slate-500 uppercase tracking-wider block font-bold mb-1">Evidence Notes / Description</span>
                                        <p className="text-slate-700 dark:text-gray-300 leading-relaxed font-medium whitespace-pre-wrap">{check.evidence_notes}</p>
                                      </div>
                                    )}

                                    {/* Image Attachment block */}
                                    {check.evidence_url && (
                                      <div className="p-2.5 bg-slate-50 dark:bg-gray-900/30 rounded border border-slate-200 dark:border-gray-800 space-y-1.5">
                                        <span className="text-[9px] text-slate-500 uppercase tracking-wider block font-bold">Attached Evidence File</span>
                                        <div className="relative group max-w-lg border border-slate-200 dark:border-gray-700/60 rounded-xl overflow-hidden bg-slate-100 dark:bg-gray-950 flex items-center justify-center p-2">
                                          <img
                                            src={check.evidence_url}
                                            alt="Compliance Evidence"
                                            className="max-h-60 object-contain hover:scale-102 transition-transform duration-200"
                                          />
                                        </div>
                                      </div>
                                    )}
                                  </div>
                                ) : check.check_evidence && check.check_evidence.length > 0 ? (
                                  <div className="space-y-2">
                                    {check.check_evidence.map((ev, idx) => (
                                      <div
                                        key={idx}
                                        className="p-2.5 bg-slate-50 dark:bg-gray-900/40 rounded border border-slate-200 dark:border-gray-800 space-y-2 text-xs"
                                      >
                                        {/* Row 1: ID, Title, Status */}
                                        <div className="flex items-start justify-between gap-3">
                                          <div className="flex items-center gap-2 flex-wrap min-w-0 flex-1">
                                            <span className="text-[9px] font-bold text-slate-900 dark:text-white font-mono bg-white dark:bg-gray-950 border border-slate-200 dark:border-gray-700 px-1 py-0.2 rounded shrink-0">
                                              {ev.check_id}
                                            </span>
                                            <span className="font-semibold text-slate-800 dark:text-gray-200 break-words">
                                              {ev.title || "Automated audit check"}
                                            </span>
                                          </div>

                                          {/* Status */}
                                          <div className="shrink-0 pt-0.5">
                                            {ev.reported === false ? (
                                              <span className="px-1.5 py-0.5 bg-slate-100 dark:bg-gray-800 text-slate-500 dark:text-gray-400 rounded text-[9px] font-bold border border-slate-200 dark:border-gray-700">
                                                Not Reported
                                              </span>
                                            ) : ev.status ? (
                                              <span className="px-1.5 py-0.5 bg-emerald-50 dark:bg-emerald-500/10 text-emerald-600 dark:text-emerald-400 rounded text-[9px] font-bold border border-emerald-200 dark:border-emerald-500/20">
                                                Pass
                                              </span>
                                            ) : (
                                              <span className="px-1.5 py-0.5 bg-rose-50 dark:bg-rose-500/10 text-rose-600 dark:text-rose-400 rounded text-[9px] font-bold border border-rose-200 dark:border-rose-500/20">
                                                Fail
                                              </span>
                                            )}
                                          </div>
                                        </div>

                                        {/* Row 2: Remediation Target & Reported Value */}
                                        <div className="grid grid-cols-1 md:grid-cols-2 gap-2 pt-1 border-t border-slate-200 dark:border-gray-800/40">
                                          {ev.remediation && (
                                            <div className="bg-white dark:bg-gray-950/30 p-1.5 rounded border border-slate-200 dark:border-gray-900">
                                              <span className="text-[8px] text-orange-600 dark:text-orange-400 block uppercase font-bold tracking-wider mb-0.5">To Remediate</span>
                                              <p className="text-[10px] text-slate-600 dark:text-gray-400 font-mono break-all leading-relaxed">
                                                {ev.remediation}
                                              </p>
                                            </div>
                                          )}

                                          <div className="bg-white dark:bg-gray-950/30 p-1.5 rounded border border-slate-200 dark:border-gray-900">
                                            <span className="text-[8px] text-slate-500 block uppercase font-bold tracking-wider mb-0.5">Reported Value</span>
                                            <p className="text-[10px] text-slate-700 dark:text-gray-300 font-mono break-all leading-relaxed">
                                              {renderCurrentValue(
                                                Array.isArray(ev.value) ? JSON.stringify(ev.value) : ev.value
                                              )}
                                            </p>
                                          </div>
                                        </div>
                                      </div>
                                    ))}
                                  </div>
                                ) : (
                                  <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                                    <div className="p-2 bg-slate-50 dark:bg-gray-950/40 rounded border border-slate-200 dark:border-gray-800">
                                      <span className="text-[9px] text-slate-500 uppercase tracking-wider block font-bold">Target Standard</span>
                                      <span className="text-xs font-mono text-slate-700 dark:text-gray-300 break-all">{check.default_value || "-"}</span>
                                    </div>
                                    <div className="p-2 bg-slate-50 dark:bg-gray-950/40 rounded border border-slate-200 dark:border-gray-800">
                                      <span className="text-[9px] text-slate-500 uppercase tracking-wider block font-bold">Current System Value</span>
                                      <span className={`text-xs font-mono break-all ${check.status ? "text-emerald-600 dark:text-emerald-400" : "text-rose-600 dark:text-rose-400 font-semibold"}`}>
                                        {renderCurrentValue(check.value)}
                                      </span>
                                    </div>
                                  </div>
                                )}
                              </div>
                            </div>
                          )}

                          {/* Tab Content 2: Remediation Details */}
                          {activeDetailTab === "remediation" && check.remediation && (
                            <div className="p-2.5 bg-white dark:bg-gray-950/30 rounded-lg border border-slate-200 dark:border-gray-700 space-y-2 shadow-sm dark:shadow-none">
                              <div className="flex items-center justify-between mb-1">
                                <span className="text-[9px] font-bold text-orange-600 dark:text-orange-400 uppercase tracking-widest flex items-center gap-1">
                                  <Info className="w-3 h-3" />
                                  Recommended Corrective Actions
                                </span>
                                <button
                                  onClick={() => handleCopy(check.remediation, `rem-${check.id}`)}
                                  className="flex items-center cursor-pointer gap-1 px-2.5 py-0.5 bg-slate-100 hover:bg-slate-200 dark:bg-gray-800 dark:hover:bg-gray-700 border border-slate-200 dark:border-gray-700 rounded transition text-[10px] font-bold text-slate-700 dark:text-gray-300 shadow-sm dark:shadow-none"
                                >
                                  {copiedField === `rem-${check.id}` ? (
                                    <>
                                      <Check className="w-2.5 h-2.5 text-emerald-600 dark:text-emerald-400" />
                                      <span className="text-emerald-600 dark:text-emerald-400">Copied!</span>
                                    </>
                                  ) : (
                                    <>
                                      <Copy className="w-2.5 h-2.5" />
                                      Copy Instructions
                                    </>
                                  )}
                                </button>
                              </div>
                              <pre className="text-xs text-slate-800 dark:text-gray-300 font-mono bg-slate-50 dark:bg-gray-950 p-2 rounded border border-slate-200 dark:border-gray-800 overflow-x-auto whitespace-pre-wrap break-all leading-relaxed">
                                {check.remediation}
                              </pre>
                            </div>
                          )}
                        </div>
                      )
                      }
                    </div>
                  );
                })}
              </div>
            )}

            {/* Pagination Controls */}
            {totalChecksCount > 0 && (
              <div className="p-2 border-t border-slate-200 dark:border-gray-700/60 bg-slate-50 dark:bg-gray-800/40 flex flex-col sm:flex-row items-center justify-between gap-3">
                {/* Entries selector */}
                <div className="flex items-center gap-1.5 text-[11px] text-slate-600 dark:text-gray-400">
                  <span>Show</span>
                  <select
                    value={pageSize}
                    onChange={(e) => setPageSize(Number(e.target.value))}
                    className="bg-white dark:bg-gray-900 border border-slate-200 dark:border-gray-700 rounded px-1.5 py-0.5 text-xs text-slate-800 dark:text-gray-200 outline-none focus:border-orange-500 shadow-sm dark:shadow-none"
                  >
                    <option value={10}>10</option>
                    <option value={25}>25</option>
                    <option value={50}>50</option>
                    <option value={100}>100</option>
                  </select>
                  <span>entries</span>
                  <span className="mx-1 text-slate-300 dark:text-gray-600">|</span>
                  <span>
                    Showing <span className="font-extrabold text-slate-900 dark:text-white">{(page - 1) * pageSize + 1}</span> to{" "}
                    <span className="font-extrabold text-slate-900 dark:text-white">
                      {Math.min(page * pageSize, totalChecksCount)}
                    </span>{" "}
                    of <span className="font-extrabold text-slate-900 dark:text-white">{totalChecksCount}</span>
                  </span>
                </div>

                {/* Prev / Next buttons */}
                <div className="flex items-center gap-1 flex-row">
                  <button
                    onClick={() => setPage(p => Math.max(1, p - 1))}
                    disabled={page === 1}
                    className="p-1 rounded bg-white dark:bg-gray-900 border border-slate-200 dark:border-gray-700 text-slate-600 dark:text-gray-400 hover:text-slate-900 dark:hover:text-white disabled:opacity-40 disabled:hover:text-slate-400 cursor-pointer disabled:cursor-not-allowed transition shadow-sm dark:shadow-none"
                  >
                    <ChevronLeft className="w-3.5 h-3.5" />
                  </button>

                  {/* Page numbers list */}
                  {Array.from({ length: totalPages }).map((_, idx) => {
                    const pageNum = idx + 1;
                    if (
                      pageNum === 1 ||
                      pageNum === totalPages ||
                      Math.abs(pageNum - page) <= 1
                    ) {
                      return (
                        <button
                          key={pageNum}
                          onClick={() => setPage(pageNum)}
                          className={`px-2.5 py-1 text-xs font-bold rounded-lg cursor-pointer transition-all duration-150 ${page === pageNum
                            ? "bg-gradient-to-r from-orange-600 to-amber-500 text-white font-extrabold shadow-md shadow-orange-500/10"
                            : "bg-white dark:bg-gray-950 text-slate-600 dark:text-gray-400 border border-slate-200 dark:border-gray-800 hover:bg-slate-100 dark:hover:bg-gray-800 hover:text-slate-900 dark:hover:text-white shadow-sm dark:shadow-none"
                            }`}
                        >
                          {pageNum}
                        </button>
                      );
                    }
                    if (
                      pageNum === 2 ||
                      pageNum === totalPages - 1
                    ) {
                      return <span key={pageNum} className="text-slate-400 dark:text-gray-650 text-[10px] px-0.5">...</span>;
                    }
                    return null;
                  })}

                  <button
                    onClick={() => setPage(p => Math.min(totalPages, p + 1))}
                    disabled={page === totalPages}
                    className="p-1 rounded bg-white dark:bg-gray-900 border border-slate-200 dark:border-gray-700 text-slate-600 dark:text-gray-400 hover:text-slate-900 dark:hover:text-white disabled:opacity-40 disabled:hover:text-slate-400 cursor-pointer disabled:cursor-not-allowed transition shadow-sm dark:shadow-none"
                  >
                    <ChevronRight className="w-3.5 h-3.5" />
                  </button>
                </div>
              </div>
            )}
          </div>
        </>
      )
      }
      {/* Evidence Upload Modal */}
      {
        evidenceModalOpen && selectedCheckForEvidence && (
          <div
            className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/80 backdrop-blur-md animate-in fade-in duration-300"
            onClick={(e) => {
              if (e.target === e.currentTarget && !isUploading) handleCloseEvidenceModal();
            }}
          >
            <div className="bg-white dark:bg-[#111827] border border-slate-200 dark:border-gray-700/50 rounded-2xl w-full max-w-lg shadow-2xl animate-in zoom-in duration-300 p-6 relative flex flex-col">
              <button
                type="button"
                onClick={handleCloseEvidenceModal}
                disabled={isUploading}
                className="absolute top-4 right-4 text-slate-400 hover:text-slate-700 dark:text-gray-400 dark:hover:text-white disabled:opacity-50 transition cursor-pointer"
              >
                <X className="w-5 h-5" />
              </button>

              <div className="flex items-center gap-3 mb-5">
                <div className="w-10 h-10 rounded-xl bg-orange-500/10 border border-orange-500/20 flex items-center justify-center shrink-0">
                  <Upload className="text-orange-600 dark:text-orange-500 w-5 h-5" />
                </div>
                <div>
                  <h3 className="text-sm font-bold text-slate-900 dark:text-white uppercase tracking-wider">Upload Manual Evidence</h3>
                  <p className="text-xs text-slate-500 dark:text-gray-400 mt-0.5">Provide proof of compliance for this manual requirement.</p>
                </div>
              </div>

              <div className="bg-slate-50 dark:bg-gray-900 border border-slate-200 dark:border-gray-800/80 rounded-xl p-4 mb-4 text-xs text-slate-700 dark:text-gray-300">
                <div className="flex items-center gap-1.5 mb-1 flex-wrap">
                  <span className="text-[10px] font-bold text-slate-900 dark:text-white font-mono bg-white dark:bg-gray-950 border border-slate-200 dark:border-gray-700 px-1 py-0.2 rounded font-black shrink-0">
                    {selectedCheckForEvidence.compliance_id || selectedCheckForEvidence.check_id}
                  </span>
                  <span className="font-semibold text-slate-800 dark:text-gray-200">{selectedCheckForEvidence.title}</span>
                </div>
              </div>

              <form onSubmit={handleUploadEvidence} className="space-y-4">
                {/* Evidence File Input */}
                <div>
                  <label className="block text-[11px] font-bold text-slate-600 dark:text-gray-400 uppercase tracking-wider mb-1.5">
                    Evidence Image (PNG, JPEG) <span className="text-rose-500">*</span>
                  </label>
                  <div className="relative">
                    <input
                      type="file"
                      ref={evidenceFileInputRef}
                      onChange={handleEvidenceFileChange}
                      accept="image/png, image/jpeg, image/jpg"
                      className="hidden"
                    />
                    {evidenceFile ? (
                      <div className="flex items-center justify-between bg-slate-50 dark:bg-gray-900 border border-slate-200 dark:border-gray-700 rounded-lg px-3 py-2 text-xs text-slate-800 dark:text-gray-200">
                        <div className="flex items-center gap-2 truncate">
                          <Check className="w-4 h-4 text-emerald-600 dark:text-emerald-400 shrink-0" />
                          <span className="truncate font-semibold">{evidenceFile.name}</span>
                          <span className="text-[10px] text-slate-500 dark:text-gray-500 shrink-0">({(evidenceFile.size / 1024).toFixed(1)} KB)</span>
                        </div>
                        <button
                          type="button"
                          onClick={() => setEvidenceFile(null)}
                          className="text-rose-600 dark:text-rose-400 hover:text-rose-700 dark:hover:text-rose-300 font-bold ml-2 cursor-pointer"
                        >
                          Remove
                        </button>
                      </div>
                    ) : (
                      <button
                        type="button"
                        onClick={() => evidenceFileInputRef.current?.click()}
                        className="w-full py-4 border border-dashed border-slate-300 dark:border-gray-700 hover:border-orange-500/50 hover:bg-orange-500/5 rounded-xl flex flex-col items-center justify-center text-xs text-slate-500 dark:text-gray-400 hover:text-slate-800 dark:hover:text-gray-200 transition cursor-pointer select-none"
                      >
                        <Upload className="w-5 h-5 text-slate-400 dark:text-gray-500 mb-1.5" />
                        <span className="font-bold">Choose an image file</span>
                        <span className="text-[10px] text-slate-400 dark:text-gray-500 mt-0.5">Drag & drop or click to browse</span>
                      </button>
                    )}
                    {evidenceError && (
                      <p className="text-[10px] text-rose-600 dark:text-rose-400 mt-1 font-bold">{evidenceError}</p>
                    )}
                  </div>
                </div>

                {/* Evidence Description / Notes */}
                <div>
                  <label className="block text-[11px] font-bold text-slate-600 dark:text-gray-400 uppercase tracking-wider mb-1.5">
                    Evidence Notes / Description
                  </label>
                  <textarea
                    value={evidenceNotes}
                    onChange={(e) => setEvidenceNotes(e.target.value)}
                    rows={4}
                    placeholder="Describe how the configuration complies with the requirement..."
                    className="w-full bg-white dark:bg-gray-900 border border-slate-200 dark:border-gray-700 hover:border-slate-300 dark:hover:border-gray-650 focus:border-orange-500 rounded-xl p-3 text-xs text-slate-900 dark:text-gray-100 placeholder-slate-400 dark:placeholder-gray-500 outline-none transition resize-none shadow-sm dark:shadow-none"
                  />
                </div>

                {/* Action Buttons */}
                <div className="flex justify-end gap-3 pt-2">
                  <button
                    type="button"
                    onClick={handleCloseEvidenceModal}
                    disabled={isUploading}
                    className="px-4 py-2 border border-slate-200 dark:border-gray-700 rounded-xl text-xs font-bold text-slate-600 dark:text-gray-400 hover:bg-slate-100 dark:hover:bg-gray-800 hover:text-slate-900 dark:hover:text-white transition-all disabled:opacity-50 cursor-pointer shadow-sm dark:shadow-none"
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    disabled={isUploading || !evidenceFile}
                    className="px-4 py-2 bg-gradient-to-r from-orange-600 to-amber-500 hover:from-orange-500 hover:to-amber-400 disabled:from-slate-200 disabled:to-slate-300 dark:disabled:from-gray-800 dark:disabled:to-gray-800 disabled:text-slate-400 dark:disabled:text-gray-500 text-white rounded-xl text-xs font-bold transition-all flex items-center gap-1.5 cursor-pointer shadow-md shadow-orange-600/10"
                  >
                    {isUploading ? (
                      <>
                        <RefreshCw className="w-3.5 h-3.5 animate-spin" />
                        Uploading...
                      </>
                    ) : (
                      <>
                        <Check className="w-3.5 h-3.5" />
                        Submit Evidence
                      </>
                    )}
                  </button>
                </div>
              </form>
            </div>
          </div>
        )
      }
    </div >
  );
};

export default NewAssetComplianceDashboard;
